import { Image, Popup } from "antd-mobile";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchOrderDetail } from "~/api/endpoints";
import { IconCircleLoadingOrange } from "~/components/Iconfont/Iconfont";
import { formatAmount } from "~/utils/number-format";
import SwapStatusOk from "~/assets/imgs/swap_status_ok.png";
import SwapStatusOkDark from "~/assets/imgs/swap_status_ok_dark.png";
import SwapStatusFail from "~/assets/imgs/swap_status_fail.png";
import SwapStatusFailDark from "~/assets/imgs/swap_status_fail_dark.png";
import SwapRightIcon from "~/assets/imgs/swap_right_close.png";
import SwapRightIconDark from "~/assets/imgs/swap_right_close_dark.png";
import { themeParams, useSignal } from "@telegram-apps/sdk-react";
import { useNavigate } from "react-router";
import { ORDER_DIRECTION, ORDER_STATUS } from "~/pages/constants";
import useSWR from "swr";
import { Spinner } from "@telegram-apps/telegram-ui";
import { useAppSelector } from "~/store";
import { TradeOrder } from "~/types";
import { getSafeAreaInsetTop } from "~/components/widget/SafeAreaTop/SafeAreaTop";

type TradeResultPopupProps = {
    tradeOrderId?: string;
    setTradeOrderId: (order_id?: string | undefined) => void
};
const TradeResultPopupWrapper: React.FC<TradeResultPopupProps> = ({
    tradeOrderId,
    setTradeOrderId
}) => {
    const [isOpenOrderPopup, setIsOpenOrderPopup] = useState(false);

    useEffect(() => {
        if (Boolean(tradeOrderId != null && tradeOrderId.length > 0)) {
            setIsOpenOrderPopup(true);
        }
    }, [tradeOrderId]);

    if (!isOpenOrderPopup) return null;

    return (
        <TradeResultPopup
            tradeOrderId={tradeOrderId!}
            setIsOpenOrderPopup={setIsOpenOrderPopup}
            setTradeOrderId={setTradeOrderId}
        />
    );
};

const TradeResultPopup: React.FC<{
    tradeOrderId: string;
    setTradeOrderId: (order_id?: string | undefined) => void;
    setIsOpenOrderPopup: (val: boolean) => void;
}> = ({ tradeOrderId: order_id, setIsOpenOrderPopup, setTradeOrderId }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isDark = useSignal(themeParams.isDark);

    const tradeOrder = useAppSelector((state) => {
        return state.trade.order as unknown as TradeOrder;
    });

    const fetchDetail = useCallback((args: any) => {
        const [_, order_id] = args;
        if (!order_id) return null;
        return fetchOrderDetail(order_id).then((res) => {
            if (res.data.detail.status === ORDER_STATUS.COMPLETED) {
                setTimeout(handleClosePopup, 3000);
            }
            return res.data.detail;
        });
    }, []);

    const { data } = useSWR(
        ["TradeResultPopup:IntrevalFetchOrderDetail", order_id],
        fetchDetail,
        {
            refreshInterval: 3000,
            fallbackData: tradeOrder,
        }
    );

    const handleClosePopup = () => {
        setTradeOrderId(undefined);
        setIsOpenOrderPopup(false);
    };

    const actionText =
        data?.direction === ORDER_DIRECTION.buy ? t("buy") : t("sell");
    const currentToken = useMemo(() => {
        if (data?.direction === ORDER_DIRECTION.buy) {
            return data?.to_token;
        } else {
            return data?.from_token;
        }
    }, [data]);

    const stateMap = {
        1: {
            icon: (
                <div
                    className={clsx(
                        "w-[32px] h-[32px] rounded-full flex justify-center items-center",
                        isDark ? "bg-DarkAlphaOrange" : "bg-AlphaOrange"
                    )}
                >
                    <IconCircleLoadingOrange className="text-[12px] loading-rotate-1s" />
                </div>
            ),
            text: t("trade_is_pending", { actionName: actionText }),
        },
        6: {
            icon: (
                <Image
                    src={isDark ? SwapStatusOkDark : SwapStatusOk}
                    className="w-[32px] h-[32px]"
                />
            ),
            text: t("trade_is_success", { actionName: actionText }),
        },
        9: {
            icon: (
                <Image
                    src={isDark ? SwapStatusFailDark : SwapStatusFail}
                    className="w-[32px] h-[32px]"
                />
            ),
            text: t("trade_is_failed", { actionName: actionText }),
        },
    };

    const status = Number(data?.status ?? 1) as 1 | 6 | 9;
    const icon = stateMap[status].icon;
    const text = stateMap[status].text;

    return (
        <Popup
            visible={true}
            onClose={handleClosePopup}
            position="top"
            closeOnSwipe={true}
            mask={false}
            stopPropagation={["click", "touchstart"]}
            bodyClassName="flex flex-col bg-none"
            bodyStyle={{
                backgroundColor: "transparent",
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
                padding: "8px",
                height: "30vh",
                marginTop: getSafeAreaInsetTop(),
            }}
        >
            <>
                <div
                    className={clsx(
                        "bg-bg_top p-4 rounded-[16px] border-[0.5px] border-border_2",
                        "flex gap-4"
                    )}
                    style={{
                        boxShadow: `0px 8px 20px 0px rgba(0, 0, 0, ${isDark ? "0.4" : "0.1"})`,
                    }}
                >
                    {data ? (
                        <>
                            <div className="flex flex-none">{icon}</div>
                            <div
                                className="flex-auto"
                                onClick={() =>
                                    navigate(
                                        `/trade-detail?order_id=${data?.order_id}&chain_id=${currentToken?.chain_id}&contract=${currentToken?.contract}`
                                    )
                                }
                            >
                                <p className="text-body1 text-text_1 font-semibold">
                                    {data?.from_token?.symbol?.toUpperCase?.()}:{" "}
                                    {text}
                                </p>
                                <p className="mt-2">
                                    <span className="text-body3 text-text_2 mr-1 ">
                                        {t("pay")}
                                    </span>
                                    <span className="text-body3 text-text_sell mr-1">
                                        {formatAmount(
                                            data?.from_token?.amount ?? 0
                                        )}{" "}
                                        {data?.from_token?.symbol?.toUpperCase?.()}
                                    </span>
                                </p>
                                <p>
                                    <span className="text-body3 text-text_2 mr-1">
                                        {t("get")}
                                    </span>
                                    <span className="text-body3 text-text_buy mr-1">
                                        {formatAmount(
                                            data?.to_token.amount ?? 0
                                        )}{" "}
                                        {data?.to_token?.symbol?.toUpperCase?.()}
                                    </span>
                                </p>
                            </div>
                            <span className="flex flex-none">
                                {/* <IconModalClose onClick={handleClosePopup} /> */}
                                <Image
                                    src={
                                        isDark
                                            ? SwapRightIconDark
                                            : SwapRightIcon
                                    }
                                    className="w-[24px] h-[24px] cursor-pointer"
                                    onClick={handleClosePopup}
                                ></Image>
                            </span>
                        </>
                    ) : (
                        <div className="w-full flex items-center justify-center">
                            <Spinner size="m" />
                        </div>
                    )}
                </div>
            </>
        </Popup>
    );
};

export default TradeResultPopupWrapper;
